<template>
    <div class="bg-white q-my-lg" id="benchmark-performance-chart">
        <div class="row bg-primary q-pa-sm q-px-md q-py-sm">
            <span class="text-h5 text-weight-medium text-white q-px-sm">
                {{ $t('Downtime and Uptime') }}
            </span>
        </div>
        <v-chart :option="chartOptions" autoresize class="graph" />
    </div>
</template>

<script>
import { BarChart } from 'echarts/charts';
import { TitleComponent, TooltipComponent } from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';
import { colors } from 'quasar';
const { getPaletteColor } = colors;

use([CanvasRenderer, BarChart, TitleComponent, TooltipComponent]);

export default {
    name: 'BenchmarkPerformanceChart',
    components: { VChart },
    props: {
        chartData: {
            type: Object,
            required: true,
            validator: value => {
                if (!value || typeof value !== 'object') {
                    return false;
                }

                const { running, planned, unplanned } = value;

                if (!running || !planned || !unplanned) {
                    return false;
                }

                if (
                    !Array.isArray(running) ||
                    !Array.isArray(planned) ||
                    !Array.isArray(unplanned)
                ) {
                    return false;
                }

                if (
                    running.length !== planned.length ||
                    running.length !== unplanned.length
                ) {
                    return false;
                }

                return true;
            }
        },
        facilities: {
            type: Array,
            required: true,
            validator: value => {
                return Array.isArray(value) && value.length > 0;
            }
        }
    },
    data() {
        return {
            runningTimeData: this.chartData?.running || [],
            plannedDowntimeData: this.chartData?.planned || [],
            unplannedDowntimeData: this.chartData?.unplanned || []
        };
    },
    computed: {
        /**
         * Returns the basic series data for the chart.
         * @returns {Object} The series data.
         */
        seriesData() {
            return {
                type: 'bar',
                stack: 'total',
                barWidth: '50%',
                tooltip: {
                    valueFormatter: value => value + '%'
                },
                label: {
                    show: true,
                    formatter: params =>
                        params.value === 0 ? '' : `${params.value}%`
                }
            };
        },
        /**
         * Returns the chart options.
         * @returns {Object} The chart options.
         */
        chartOptions() {
            return {
                animation: false,
                tooltip: {
                    trigger: 'axis',
                    order: 'seriesDesc',
                    axisPointer: {
                        type: 'shadow'
                    }
                },
                legend: {
                    data: [
                        this.$t('Running Time'),
                        this.$t('Planned Downtime'),
                        this.$t('Unplanned Downtime')
                    ],
                    bottom: 0
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '8%',
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                xAxis: [
                    {
                        type: 'category',
                        data: this.facilities,
                        axisTick: {
                            alignWithLabel: true
                        }
                    }
                ],
                yAxis: [
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: '{value}%'
                        },
                        max: 100
                    }
                ],
                series: [
                    {
                        ...this.seriesData,
                        name: this.$t('Running Time'),
                        color: getPaletteColor('light-green'),
                        data: this.runningTimeData
                    },
                    {
                        ...this.seriesData,
                        name: this.$t('Planned Downtime'),
                        color: getPaletteColor('warning'),
                        data: this.plannedDowntimeData
                    },
                    {
                        ...this.seriesData,
                        name: this.$t('Unplanned Downtime'),
                        color: getPaletteColor('light-red'),
                        data: this.unplannedDowntimeData
                    }
                ]
            };
        },
        /**
         * Checks if there is only one facility.
         * @returns {boolean} True if there is only one facility, false otherwise.
         */
        hasSingleFacility() {
            return this.facilities && this.facilities.length === 1;
        }
    }
};
</script>
<style lang="scss" scoped>
.graph {
    height: 500px;
    width: calc(100% - 2px);
    padding-bottom: 16px;
}
</style>
